import { useState, useEffect } from 'react';
import { builderApi } from '../api';

const CATEGORIES = [
  { key: 'trade',    label: 'Trades',         icon: '🔧', desc: 'Plumber, Mason, Electrician…' },
  { key: 'emp_role', label: 'Employee Roles',  icon: '👷', desc: 'Supervisor, Site Engineer…' },
];

const DEFAULT_TRADES    = ['Plumber', 'Electrician', 'Carpenter', 'Painter', 'Mason', 'Welder', 'Fabricator', 'Excavation Operator', 'Tiler', 'Waterproofing', 'HVAC Technician'];
const DEFAULT_EMP_ROLES = ['Admin', 'Supervisor', 'Civil Engineer', 'Mechanical Engineer', 'Electrical Engineer', 'Site Engineer', 'Accountant', 'Store Keeper'];

export default function BuilderSettings() {
  const [tab,      setTab]     = useState('trade');
  const [lookups,  setLookups] = useState([]);
  const [newVal,   setNewVal]  = useState('');
  const [loading,  setLoading] = useState(true);
  const [saving,   setSaving]  = useState(false);
  const [err,      setErr]     = useState('');

  const load = () => {
    setLoading(true);
    builderApi.getLookups().then(setLookups).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const current = lookups.filter(l => l.category === tab);

  const add = async () => {
    if (!newVal.trim()) return;
    setSaving(true); setErr('');
    try {
      await builderApi.createLookup({ category: tab, value: newVal.trim() });
      setNewVal(''); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this value?')) return;
    try { await builderApi.deleteLookup(id); load(); } catch (e) { setErr(e.message); }
  };

  const seedDefaults = async () => {
    const defaults = tab === 'trade' ? DEFAULT_TRADES : DEFAULT_EMP_ROLES;
    const existing = new Set(current.map(l => l.value.toLowerCase()));
    const toAdd = defaults.filter(v => !existing.has(v.toLowerCase()));
    if (toAdd.length === 0) { setErr('All defaults already exist'); return; }
    setSaving(true); setErr('');
    try {
      await Promise.all(toAdd.map(v => builderApi.createLookup({ category: tab, value: v })));
      load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const catInfo = CATEGORIES.find(c => c.key === tab);

  return (
    <div className="bld-card">
      <h2 style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--text-primary)' }}>Settings</h2>
      <div style={{ fontSize: 13, color: '#718096', marginBottom: 20 }}>
        Manage the dropdown values used across the construction module.
      </div>

      {err && (
        <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{err}</span>
          <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: 20 }}>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => { setTab(c.key); setNewVal(''); setErr(''); }} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px', fontSize: 13, fontWeight: 600,
            color: tab === c.key ? '#3182ce' : '#718096',
            borderBottom: tab === c.key ? '2px solid #3182ce' : '2px solid transparent', marginBottom: -2,
          }}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left: current values */}
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 10 }}>
            {catInfo?.label} ({current.length})
          </div>
          {loading ? (
            <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div>
          ) : current.length === 0 ? (
            <div style={{ color: '#718096', fontSize: 13, padding: '16px 0' }}>
              No {catInfo?.label.toLowerCase()} added yet.{' '}
              <button style={{ background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', fontSize: 13, padding: 0 }}
                onClick={seedDefaults}>Seed defaults</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {current.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'var(--bg-secondary)', borderRadius: 8, padding: '8px 12px' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                    {catInfo?.icon} {item.value}
                  </span>
                  <button onClick={() => del(item.id)}
                    style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
                    title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: add new + seed */}
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 10 }}>
            Add New {catInfo?.label.slice(0, -1)}
          </div>
          <div style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>e.g. {catInfo?.desc}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" value={newVal} onChange={e => setNewVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder={`New ${catInfo?.label.slice(0, -1).toLowerCase()}…`}
              style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={add} disabled={saving || !newVal.trim()}>Add</button>
          </div>

          <div style={{ marginTop: 24, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>Quick-seed defaults</div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>
              Adds the standard {catInfo?.label.toLowerCase()} that are not already in your list.
            </div>
            <button className="btn btn-outline" onClick={seedDefaults} disabled={saving} style={{ fontSize: 12 }}>
              Seed Default {catInfo?.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
